import React, { useState, useEffect, useContext } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  InputAdornment,
  Switch,
  FormControlLabel,
  useTheme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import EmailOutlinedIcon from "@material-ui/icons/EmailOutlined";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import { Helmet } from "react-helmet";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import appLogo from "../../assets/logo1.png";

const SHOW_SIGNUP_BUTTON = false;

const useStyles = makeStyles((theme) => {
  const isLight = theme.mode === "light";
  const primary = theme.palette.primary.main;

  return {
    root: {
      position: "relative",
      display: "flex",
      width: "100vw",
      minHeight: "100dvh",
      alignItems: "center",
      justifyContent: "center",
      padding: theme.spacing(3),
      overflow: "auto",
      background: isLight
        ? `radial-gradient(circle at top left, ${primary}22 0%, transparent 42%), radial-gradient(circle at bottom right, ${primary}18 0%, transparent 38%), ${theme.palette.fancyBackground}`
        : `radial-gradient(circle at top left, ${primary}33 0%, transparent 40%), radial-gradient(circle at bottom right, #1f2937 0%, transparent 45%), ${theme.palette.fancyBackground}`,
    },
    themeToggle: {
      position: "fixed",
      top: theme.spacing(2),
      right: theme.spacing(2),
      zIndex: 2,
      backgroundColor: isLight ? "rgba(255,255,255,0.82)" : "rgba(31,41,55,0.82)",
      border: `1px solid ${isLight ? "rgba(15,23,42,0.08)" : "rgba(255,255,255,0.08)"}`,
      boxShadow: isLight
        ? "0 8px 24px rgba(15, 23, 42, 0.08)"
        : "0 8px 24px rgba(0, 0, 0, 0.28)",
      color: isLight ? "#334155" : "#e5e7eb",
      "&:hover": {
        backgroundColor: isLight ? "#ffffff" : "#374151",
      },
    },
    shell: {
      width: "100%",
      maxWidth: 980,
      display: "grid",
      gridTemplateColumns: "1fr 420px",
      gap: theme.spacing(4),
      alignItems: "stretch",
      animation: "$fadeIn 0.55s ease-out",
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "1fr",
        maxWidth: 420,
      },
    },
    brandPanel: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      padding: theme.spacing(4, 2),
      [theme.breakpoints.down("sm")]: {
        display: "none",
      },
    },
    brandBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: theme.spacing(1),
      width: "fit-content",
      padding: "6px 12px",
      borderRadius: 999,
      marginBottom: theme.spacing(2),
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: 0.4,
      color: isLight ? primary : "#dbeafe",
      backgroundColor: isLight ? `${primary}14` : "rgba(255,255,255,0.06)",
      border: `1px solid ${isLight ? `${primary}22` : "rgba(255,255,255,0.08)"}`,
    },
    brandTitle: {
      fontSize: 42,
      lineHeight: 1.1,
      fontWeight: 800,
      color: isLight ? "#0f172a" : "#f8fafc",
      marginBottom: theme.spacing(1.5),
    },
    brandSubtitle: {
      fontSize: 16,
      lineHeight: 1.6,
      maxWidth: 420,
      color: isLight ? "#64748b" : "#94a3b8",
      marginBottom: theme.spacing(3),
    },
    featureList: {
      display: "grid",
      gap: theme.spacing(1.25),
    },
    featureItem: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.25),
      color: isLight ? "#475569" : "#cbd5e1",
      fontSize: 14,
    },
    featureDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      backgroundColor: primary,
      flexShrink: 0,
    },
    formCard: {
      borderRadius: 24,
      padding: theme.spacing(4, 3.5),
      backgroundColor: isLight ? "rgba(255,255,255,0.88)" : "rgba(17,24,39,0.88)",
      backdropFilter: "blur(18px)",
      border: `1px solid ${isLight ? "rgba(15,23,42,0.08)" : "rgba(255,255,255,0.08)"}`,
      boxShadow: isLight
        ? "0 24px 60px rgba(15, 23, 42, 0.10)"
        : "0 24px 60px rgba(0, 0, 0, 0.35)",
    },
    mobileBrand: {
      display: "none",
      textAlign: "center",
      marginBottom: theme.spacing(3),
      [theme.breakpoints.down("sm")]: {
        display: "block",
      },
    },
    logo: {
      height: 42,
      width: "auto",
      maxWidth: 180,
      objectFit: "contain",
      marginBottom: theme.spacing(1),
    },
    formTitle: {
      fontSize: 28,
      fontWeight: 700,
      color: isLight ? "#0f172a" : "#f8fafc",
      marginBottom: theme.spacing(0.5),
    },
    formSubtitle: {
      fontSize: 14,
      color: isLight ? "#64748b" : "#94a3b8",
      marginBottom: theme.spacing(3),
    },
    textField: {
      marginBottom: theme.spacing(2),
      "& .MuiOutlinedInput-root": {
        borderRadius: 14,
        backgroundColor: isLight ? "#f8fafc" : "#0f172a",
        color: isLight ? "#0f172a" : "#f8fafc",
        "& fieldset": {
          borderColor: isLight ? "#e2e8f0" : "#374151",
        },
        "&:hover fieldset": {
          borderColor: isLight ? "#cbd5e1" : "#4b5563",
        },
        "&.Mui-focused fieldset": {
          borderColor: primary,
          borderWidth: 2,
        },
      },
      "& .MuiInputLabel-root": {
        color: isLight ? "#64748b" : "#94a3b8",
        "&.Mui-focused": {
          color: primary,
        },
      },
      "& .MuiInputAdornment-root .MuiSvgIcon-root": {
        color: isLight ? "#64748b" : "#94a3b8",
      },
    },
    rememberRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: theme.spacing(2.5),
      marginTop: theme.spacing(0.5),
    },
    rememberLabel: {
      margin: 0,
      "& .MuiFormControlLabel-label": {
        fontSize: 14,
        color: isLight ? "#475569" : "#cbd5e1",
      },
    },
    switch: {
      "& .MuiSwitch-switchBase.Mui-checked": {
        color: primary,
      },
      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
        backgroundColor: primary,
      },
    },
    loginButton: {
      borderRadius: 14,
      padding: "13px 16px",
      fontWeight: 700,
      fontSize: 15,
      textTransform: "none",
      width: "100%",
      marginBottom: theme.spacing(1.5),
      color: "#fff",
      background: `linear-gradient(135deg, ${primary} 0%, ${primary}dd 100%)`,
      boxShadow: `0 10px 24px ${primary}44`,
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: `0 14px 28px ${primary}55`,
        background: `linear-gradient(135deg, ${primary} 0%, ${primary} 100%)`,
      },
    },
    registerButton: {
      borderRadius: 14,
      padding: "13px 16px",
      fontWeight: 700,
      fontSize: 15,
      textTransform: "none",
      width: "100%",
      color: isLight ? primary : "#f8fafc",
      border: `1px solid ${isLight ? `${primary}33` : "rgba(255,255,255,0.12)"}`,
      backgroundColor: isLight ? "#ffffff" : "rgba(255,255,255,0.04)",
      "&:hover": {
        backgroundColor: isLight ? `${primary}08` : "rgba(255,255,255,0.08)",
      },
    },
    errorText: {
      color: theme.palette.error.main,
      textAlign: "center",
      marginBottom: theme.spacing(2),
      fontSize: 14,
      fontWeight: 500,
      padding: theme.spacing(1.25),
      borderRadius: 12,
      backgroundColor: isLight ? "#fef2f2" : "rgba(239, 68, 68, 0.12)",
    },
    "@keyframes fadeIn": {
      "0%": {
        opacity: 0,
        transform: "translateY(16px)",
      },
      "100%": {
        opacity: 1,
        transform: "translateY(0)",
      },
    },
  };
});

const Login = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { toggleColorMode } = useContext(ColorModeContext);
  const { handleLogin } = useContext(AuthContext);
  const [user, setUser] = useState({
    email: localStorage.getItem("rememberedEmail") || "",
    password: "",
    remember: localStorage.getItem("rememberMe") === "true",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [userCreationEnabled, setUserCreationEnabled] = useState(true);

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL === "https://localhost:8090"
      ? "https://localhost:8090"
      : process.env.REACT_APP_BACKEND_URL;

  const logoSrc =
    theme.mode === "light"
      ? theme.calculatedLogoLight?.() || theme.appLogoLight || appLogo
      : theme.calculatedLogoDark?.() || theme.appLogoDark || appLogo;

  useEffect(() => {
    const fetchUserCreationStatus = async () => {
      try {
        const response = await fetch(`${backendUrl}/settings/userCreation`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch user creation status");
        }

        const data = await response.json();
        setUserCreationEnabled(data.userCreation === "enabled");
      } catch (err) {
        console.error("Error al verificar userCreation:", err);
        setUserCreationEnabled(false);
      }
    };

    fetchUserCreationStatus();
  }, [backendUrl]);

  const handleSubmit = (e) => {
    e.preventDefault();
    handleLogin(user);
  };

  return (
    <>
      <Helmet>
        <title>Iniciar Sesión - NaviCRM</title>
      </Helmet>

      <div className={classes.root}>
        <IconButton
          className={classes.themeToggle}
          onClick={toggleColorMode}
          aria-label="Cambiar tema"
        >
          {theme.mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton>

        <div className={classes.shell}>
          <div className={classes.brandPanel}>
            <span className={classes.brandBadge}>NaviCRM · WhatsApp CRM</span>
            <Typography className={classes.brandTitle}>
              Centraliza, automatiza, crece.
            </Typography>
            <Typography className={classes.brandSubtitle}>
              Gestiona conversaciones, tickets y equipos desde un solo lugar con
              una experiencia pensada para operación diaria.
            </Typography>
            <div className={classes.featureList}>
              <div className={classes.featureItem}>
                <span className={classes.featureDot} />
                Atención multicanal en tiempo real
              </div>
              <div className={classes.featureItem}>
                <span className={classes.featureDot} />
                Flujos, etiquetas y automatización
              </div>
              <div className={classes.featureItem}>
                <span className={classes.featureDot} />
                Interfaz adaptable a modo claro y oscuro
              </div>
            </div>
          </div>

          <Paper elevation={0} className={classes.formCard}>
            <div className={classes.mobileBrand}>
              <img src={logoSrc} alt="NaviCRM" className={classes.logo} />
            </div>

            <Typography className={classes.formTitle}>
              Bienvenido de nuevo
            </Typography>
            <Typography className={classes.formSubtitle}>
              Ingresa tus credenciales para continuar
            </Typography>

            {error && (
              <Typography className={classes.errorText}>{error}</Typography>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                label="Correo electrónico"
                variant="outlined"
                fullWidth
                type="email"
                autoComplete="email"
                value={user.email}
                onChange={(e) => setUser({ ...user, email: e.target.value })}
                className={classes.textField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Contraseña"
                variant="outlined"
                fullWidth
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={user.password}
                onChange={(e) => setUser({ ...user, password: e.target.value })}
                className={classes.textField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        size="small"
                        edge="end"
                        aria-label="Mostrar contraseña"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <div className={classes.rememberRow}>
                <FormControlLabel
                  className={classes.rememberLabel}
                  control={
                    <Switch
                      checked={user.remember}
                      onChange={(e) =>
                        setUser({ ...user, remember: e.target.checked })
                      }
                      color="primary"
                      className={classes.switch}
                    />
                  }
                  label="Recordarme"
                />
              </div>

              <Button
                type="submit"
                variant="contained"
                disableElevation
                className={classes.loginButton}
              >
                Iniciar sesión
              </Button>

              {SHOW_SIGNUP_BUTTON && userCreationEnabled && (
                <Button
                  component={RouterLink}
                  to="/signup"
                  variant="outlined"
                  className={classes.registerButton}
                >
                  Crear cuenta
                </Button>
              )}
            </form>
          </Paper>
        </div>
      </div>
    </>
  );
};

export default Login;
